{
  "header": {
    "eyebrow": "Charging network",
    "title": "CPO Analytics",
    "description": "Fleet overview of your charging stations: availability, socket health and faults across all locations.",
    "action": "Open EVinity dashboard"
  },
  "aside": {
    "aria": "Socket status breakdown"
  },
  "kpi": {
    "operative": {
      "label": "Operative devices",
      "description": "Stations currently available"
    },
    "chargingSockets": {
      "label": "Charging sockets",
      "description": "Sockets actively charging now"
    },
    "faultedSockets": {
      "label": "Faulted sockets",
      "description": "Sockets reporting a fault"
    },
    "devicesOffline": {
      "label": "Devices offline",
      "description": "Stations not reachable"
    }
  },
  "map": {
    "title": "Locations",
    "error": "Couldn't load location data - the map may be incomplete.",
    "stationCount": "{{count}} station",
    "stationCount_other": "{{count}} stations",
    "filter": {
      "all": "All ({{count}})",
      "critical": "Offline ({{count}})",
      "warning": "Faulted ({{count}})",
      "healthy": "Healthy ({{count}})"
    }
  },
  "socketStatus": {
    "title": "Current socket status",
    "available": "Available",
    "occupied": "Occupied",
    "reserved": "Reserved",
    "inoperative": "Inoperative",
    "faulted": "Faulted",
    "unknown": "Unknown"
  },
  "socketSubStatus": {
    "title": "Occupied socket detail",
    "preparing": "Preparing",
    "charging": "Charging",
    "suspended_ev": "Suspended (EV)",
    "suspended_evse": "Suspended (EVSE)",
    "finishing": "Finishing"
  },
  "status": {
    "available": "Available",
    "occupied": "Occupied",
    "reserved": "Reserved",
    "inoperative": "Inoperative",
    "faulted": "Faulted",
    "offline": "Offline",
    "unknown": "Unknown"
  },
  "list": {
    "title": "Charging stations",
    "count": "{{visible}} of {{total}}",
    "sockets": "{{count}} socket",
    "sockets_other": "{{count}} sockets",
    "searchPlaceholder": "Search by ID, location or model",
    "searchAria": "Search charging stations",
    "openSelected": "Open selected ({{count}})",
    "selectAllAria": "Select all charging stations",
    "selectRowAria": "Select {{id}}",
    "filters": {
      "aria": "Filter stations",
      "status": "Status",
      "location": "Location",
      "locationAria": "Filter by location",
      "allLocations": "All locations"
    },
    "columns": {
      "station": "Station",
      "location": "Location",
      "status": "Status",
      "model": "Model",
      "power": "Max power",
      "lastSeen": "Last seen",
      "actions": "Actions"
    },
    "openInCpms": "Open {{id}} in CPMS",
    "lastSeen": {
      "never": "Never",
      "justNow": "Just now",
      "minutes": "{{count}} min ago",
      "hours": "{{count}}h ago",
      "days": "{{count}}d ago"
    },
    "empty": {
      "title": "No charging stations",
      "description": "No stations match the current filters."
    }
  },
  "faulted": {
    "title": "Faulted sockets",
    "count": "{{count}} faulted",
    "columns": {
      "station": "Station",
      "socket": "Socket",
      "error": "Error",
      "actions": "Actions"
    },
    "openInCpms": "Open {{id}} in CPMS",
    "empty": {
      "title": "No faults",
      "description": "All sockets are operating normally."
    }
  },
  "error": {
    "title": "Couldn't load fleet data",
    "description": "The charging-station management system could not be reached. Please try again later."
  },
  "fleetTransactions": {
    "title": "Ongoing transactions",
    "ongoing": "Ongoing transactions",
    "suspendedEvse": "Suspended (EVSE)",
    "count": "{{count}} ongoing",
    "searchPlaceholder": "Search by tag or station",
    "searchAria": "Search ongoing transactions",
    "columns": {
      "location": "Location",
      "socket": "Socket",
      "subStatus": "Sub-status",
      "power": "Max power",
      "started": "Started",
      "charged": "Charged"
    },
    "empty": { "title": "No ongoing transactions" },
    "error": {
      "title": "Couldn't load ongoing transactions",
      "description": "The charging-station management system could not be reached. Please try again later."
    }
  },
  "combined": {
    "title": "Combined analytics",
    "count": "{{count}} charging station",
    "count_other": "{{count}} charging stations",
    "sectionTitle": "Selected charging stations",
    "stationsTitle": "Charging stations",
    "stationsCount": "{{count}} selected",
    "notFound": {
      "title": "Stations not found",
      "description": "None of the selected charging stations could be found."
    }
  },
  "detail": {
    "eyebrow": "Charging station",
    "noLocation": "No location",
    "rangeAria": "Time range",
    "tabs": { "financial": "Financial", "technical": "Technical", "transactions": "Transactions" },
    "range": { "7": "7 days", "30": "30 days", "180": "180 days" },
    "notFound": {
      "title": "Station not found",
      "description": "No charging station matches \"{{id}}\".",
      "back": "Back to fleet"
    },
    "noData": { "title": "No data", "description": "No data for the selected period." },
    "info": {
      "title": "Charging station details",
      "deviceDetails": "Device details",
      "address": "Address",
      "customName": "Custom name",
      "manufacturer": "Manufacturer",
      "model": "Model",
      "locationStatus": "Location & status",
      "location": "Location",
      "status": "Status",
      "lastSeen": "Last seen",
      "availability": "Availability",
      "maxPower": "Max power",
      "mapTitle": "Location"
    },
    "kpi": {
      "transactions": "Transactions",
      "kwh": "kWh charged",
      "uptime": "Uptime",
      "co2": "CO₂ saved"
    },
    "income": { "title": "Total income", "series": "Income", "total": "Total" },
    "txTimeline": {
      "title": "Transactions over time",
      "series": "Transactions",
      "avg": "Avg/day",
      "max": "Max/day"
    },
    "uniqueUsers": {
      "title": "Unique users",
      "series": "Users",
      "count": "Users",
      "perUser": "Charges/user"
    },
    "paymentMethod": {
      "title": "Payment method",
      "qr": "QR code",
      "app": "Mobile app",
      "local": "Local auth"
    },
    "txStatus": { "title": "Transaction status", "successful": "Successful", "error": "Error" },
    "duration": { "title": "Charge duration", "short": "< 1h", "medium": "1–4h", "long": "4h+" },
    "consumptionTime": { "title": "Time of day", "day": "Day (08–20h)", "night": "Night (20–08h)" },
    "average": {
      "title": "Average transaction",
      "income": "Income",
      "duration": "Duration",
      "consumption": "Consumption",
      "distributionTitle": "Transaction value distribution",
      "lt5": "< €5",
      "b5_10": "€5–10",
      "b10_15": "€10–15",
      "b15_20": "€15–20",
      "gte20": "> €20"
    },
    "incomeBreakdown": {
      "title": "Income breakdown",
      "energy": "Energy",
      "time": "Charging time",
      "parking": "Parking time",
      "flat": "Flat fee"
    },
    "technical": {
      "available": "Available",
      "faulted": "Faulted",
      "inoperative": "Inoperative",
      "offline": "Offline",
      "unknown": "Unknown",
      "availablePerDay": "Available hours/day",
      "availableSeries": "Available hours",
      "durations": "Time in status"
    },
    "transactions": {
      "title": "Transactions",
      "count": "{{count}} transactions",
      "active": "Active",
      "completed": "Completed",
      "columns": {
        "started": "Started",
        "socket": "Socket",
        "energy": "Energy",
        "duration": "Duration",
        "status": "Status"
      },
      "empty": { "title": "No transactions" }
    }
  }
}
